import React, { useState, useEffect } from 'react'
import { List, Avatar, Badge, Spin, Typography, Button, Empty } from 'antd'
import { internalChatsApi } from '../../services/api'
import { useAuthStore } from '../../store/authStore'
import { format } from 'date-fns'
import { ru } from 'date-fns/locale'
import { useNavigate } from 'react-router-dom'
import {
    WechatOutlined
} from '@ant-design/icons'

interface ChatNotificationListProps {
    onClose: () => void
}

export const ChatNotificationList: React.FC<ChatNotificationListProps> = ({ onClose }) => {
    const { user } = useAuthStore()
    const navigate = useNavigate()
    const [chats, setChats] = useState<any[]>([])
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        fetchChats()
    }, [])

    const fetchChats = async () => {
        setLoading(true)
        try {
            const response = await internalChatsApi.getMyChats()
            // Filter to show only chats with unread messages or recent ones?
            // User said "notifications", implying unread.
            // But usually a quick list shows recent chats.
            // Let's sort by unread first, then date.
            const sortedChats = response.data.sort((a: any, b: any) => {
                if (a.unread_count !== b.unread_count) {
                    return b.unread_count - a.unread_count
                }
                const dateA = new Date(a.last_message?.created_at || 0).getTime()
                const dateB = new Date(b.last_message?.created_at || 0).getTime()
                return dateB - dateA
            })
            setChats(sortedChats)
        } catch (error) {
            console.error('Failed to load chats', error)
        } finally {
            setLoading(false)
        }
    }

    const handleChatClick = (chatId: number) => {
        navigate(`/chats?tab=internal&chatId=${chatId}`)
        onClose()
    }

    if (loading) {
        return <div style={{ padding: 20, textAlign: 'center' }}><Spin /></div>
    }

    if (chats.length === 0) {
        return (
            <div style={{ width: 300, padding: 20, textAlign: 'center' }}>
                <Empty description="Нет сообщений" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                <Button type="link" onClick={() => {
                    navigate('/chats?tab=internal')
                    onClose()
                }}>
                    Перейти ко всем чатам
                </Button>
            </div>
        )
    }

    return (
        <div style={{ width: 320, maxHeight: 400, overflow: 'auto', background: 'var(--bg-surface)' }}>
            <div style={{
                padding: '12px 16px',
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                position: 'sticky',
                top: 0,
                background: 'var(--bg-surface)',
                zIndex: 1
            }}>
                <span style={{ fontWeight: 600 }}>Сообщения</span>
                <Button type="link" size="small" onClick={() => {
                    navigate('/chats?tab=internal')
                    onClose()
                }}>
                    Все чаты
                </Button>
            </div>
            <List
                dataSource={chats}
                renderItem={(chat) => (
                    <List.Item
                        className="notification-item"
                        onClick={() => handleChatClick(chat.id)}
                        style={{
                            padding: '12px 16px',
                            cursor: 'pointer',
                            borderBottom: '1px solid var(--border)',
                            transition: 'background 0.2s',
                            background: chat.unread_count > 0 ? 'rgba(24, 144, 255, 0.05)' : 'transparent'
                        }}
                    >
                        <List.Item.Meta
                            avatar={
                                <Badge count={chat.unread_count} size="small">
                                    <Avatar
                                        src={chat.other_user.avatar_url}
                                        style={{ backgroundColor: '#1890ff' }}
                                        icon={<WechatOutlined />}
                                    >
                                        {(chat.other_user.full_name || chat.other_user.username || 'U')[0].toUpperCase()}
                                    </Avatar>
                                </Badge>
                            }
                            title={
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography.Text strong={chat.unread_count > 0} style={{ fontSize: 13, maxWidth: 140 }} ellipsis>
                                        {chat.other_user.full_name || chat.other_user.username}
                                    </Typography.Text>
                                    {chat.last_message && (
                                        <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                                            {format(new Date(chat.last_message.created_at), 'HH:mm', { locale: ru })}
                                        </Typography.Text>
                                    )}
                                </div>
                            }
                            description={
                                <div style={{
                                    fontSize: 12,
                                    color: 'var(--text-secondary)',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: 240
                                }}>
                                    {chat.last_message ? (
                                        <span>
                                            {chat.last_message.sender_id === user?.id && <span style={{ color: '#1890ff' }}>Вы: </span>}
                                            {chat.last_message.text_content}
                                        </span>
                                    ) : (
                                        <i style={{ color: 'var(--text-muted)' }}>Нет сообщений</i>
                                    )}
                                </div>
                            }
                        />
                    </List.Item>
                )}
            />
        </div>
    )
}
